import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { getToken, setToken, clearToken } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

export default function Portal() {
  const { type } = useParams();
  const nav = useNavigate();
  const portalType = type === "investor" ? "investor" : "distributor";
  const label = portalType === "investor" ? "Investor" : "Distributor";

  const [mode, setMode] = useState("login");
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company: "" });

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    api.get("/portal/me").then((r) => {
      if (r.data.portal_type === portalType) setUser(r.data);
      else clearToken();
    }).catch(() => clearToken());
  }, [portalType]);

  useEffect(() => {
    if (user) api.get("/portal/dashboard").then((r) => setDashboard(r.data));
  }, [user]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === "login" ? "/portal/login" : "/portal/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password, portal_type: portalType }
        : { ...form, portal_type: portalType };
      const { data } = await api.post(url, payload);
      setToken(data.token);
      setUser(data.user);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    }
    setLoading(false);
  }, [form, mode, portalType]);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    setDashboard(null);
    nav(`/portal/${portalType}`);
  }, [nav, portalType]);

  if (user && dashboard) {
    return (
      <div className="min-h-[80vh] bg-muted/30" data-testid={`${portalType}-dashboard`}>
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex justify-between items-start mb-10">
            <div>
              <div className="eyebrow">{label} Portal</div>
              <h1 className="font-display font-bold text-4xl tracking-tighter mt-2">Welcome, {user.full_name}</h1>
              <p className="text-muted-foreground text-sm mt-1">{user.email}</p>
            </div>
            <button onClick={logout} className="text-sm text-muted-foreground hover:text-gold-500" data-testid="portal-logout">Logout →</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {Object.entries(dashboard.stats).map(([k, v]) => (
              <div key={k} className="p-6 rounded-2xl bg-background border border-border">
                <div className="font-display font-black text-3xl gold-gradient-text">{v}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <div className="p-8 rounded-2xl bg-background border border-border">
            <div className="eyebrow mb-4">Resources</div>
            <ul className="divide-y divide-border">
              {dashboard.resources.map((r) => (
                <li key={r.title} className="flex justify-between items-center py-4">
                  <div>
                    <div className="font-display font-semibold">{r.title}</div>
                    <div className="text-xs text-muted-foreground">{r.type}</div>
                  </div>
                  <button className="text-sm text-gold-500 hover:text-gold-400 font-semibold" data-testid={`resource-${r.title.replace(/\s+/g, '-').toLowerCase()}`}>Download →</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-24 bg-muted/30" data-testid={`${portalType}-auth`}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-gold-400/15 flex items-center justify-center mb-4">
            <Lock className="w-5 h-5 text-gold-500" />
          </div>
          <div className="eyebrow">{label} Portal</div>
          <h1 className="font-display font-bold text-3xl mt-2">{mode === "login" ? "Sign in to continue" : "Create your account"}</h1>
        </div>
        <form onSubmit={submit} className="bg-background rounded-2xl border border-border p-6 space-y-4" data-testid="portal-form">
          {mode === "register" && (
            <>
              <F label="Full name *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testId="portal-name" />
              <F label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="portal-company" />
            </>
          )}
          <F label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="portal-email" />
          <F label="Password *" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testId="portal-password" />
          <button disabled={loading} className="w-full bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full h-11 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="portal-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "login" ? "Sign In" : "Create Account"}
          </button>
          <div className="text-center text-sm text-muted-foreground">
            {mode === "login" ? "New here?" : "Already registered?"}{" "}
            <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-gold-500 font-semibold" data-testid="portal-switch-mode">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </form>
        <div className="text-center mt-6 text-xs text-muted-foreground">
          <Link to={`/portal/${portalType === "investor" ? "distributor" : "investor"}`} className="hover:text-gold-500">
            Switch to {portalType === "investor" ? "Distributor" : "Investor"} Portal →
          </Link>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{children}</div>;
const F = ({ label, value, onChange, required, type = "text", testId }) => (
  <div>
    <Label>{label}</Label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid={testId} />
  </div>
);
